import React, { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import ModalLayout from '../layouts/ModalLayout';
import Button from '../buttons/Button';
import {
  BankMutation,
  CreateBankTransactionModel,
  TransactionType,
} from '../../../types/bankAccount';
import { buildTransactionFromMutation } from '../../../utils/bankAccountUtil';
import Input from '../misc/Input';
import WithTooltip from '../misc/WithTooltip';

interface MutationIntoTransactionsModalProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  mutation: BankMutation;
  onTransactionSplit: (transactions: CreateBankTransactionModel[]) => void;
}

export default function MutationIntoTransactionsModal({
  open,
  setOpen,
  onTransactionSplit,
  mutation,
}: MutationIntoTransactionsModalProps): JSX.Element {
  const { t } = useTranslation('', { keyPrefix: 'page.financial.mutationIntoTransactionModal' });
  const [numberOfTransactions, setNumberOfTransactions] = React.useState<string>('2');
  const [isTransactionView, setIsTransactionView] = React.useState<boolean>(false);
  const [isMultiple, setIsMultiple] = React.useState<boolean>();
  const [transactions, setTransactions] = React.useState<CreateBankTransactionModel[]>([]);

  if (mutation.bankTransactionCount > 0) {
    return (
      <ModalLayout
        open={open}
        setOpen={setOpen}
        className="w-full max-w-2xl rounded-lg"
        closeButton>
        <div className="rounded-md bg-secondary-50 p-6">{t('alreadySplit')}</div>
      </ModalLayout>
    );
  }

  return (
    <ModalLayout open={open} setOpen={setOpen} className="w-full max-w-2xl rounded-lg" closeButton>
      <div className="rounded-md bg-secondary-50 p-6">
        <h2 className="text-lg font-medium">{t('title')}</h2>
        <p className="text-opacity-80">
          {isMultiple === undefined && t('subtitle')}
          {isMultiple && !isTransactionView && t('howManySubtitle')}
          {isTransactionView && t('transactionViewSubtitle')}
        </p>

        {isMultiple === undefined && (
          <div className="flex items-center justify-center gap-4 pt-4">
            <Button
              onClick={() => onTransactionSplit([buildTransactionFromMutation(mutation)])}
              variant="primary-dark">
              {t('singleTransaction')}
            </Button>
            <WithTooltip tooltip={t('comingSoon')} className="bg-secondary">
              <Button onClick={() => setIsMultiple(true)} variant="primary-dark" disabled>
                {t('multipleTransactions')}
              </Button>
            </WithTooltip>
          </div>
        )}
        {isMultiple && !isTransactionView && (
          <div className="flex flex-col gap-2">
            <Input
              min={2}
              step={1}
              type="number"
              className="w-48 invalid:border-danger"
              onChange={(e) => setNumberOfTransactions(e.target.value)}
              value={numberOfTransactions}
            />
            <Button
              className="self-end"
              onClick={() => {
                setIsTransactionView(true);
                const transactionType =
                  mutation.amount >= 0
                    ? TransactionType.GROUP_INCOME
                    : TransactionType.GROUP_EXPENSE;

                setTransactions(
                  new Array(parseInt(numberOfTransactions, 10)).fill(0).map((_, idx) => ({
                    mutationId: mutation.mutationId,
                    transactionType,
                    amount: 0,
                    date: mutation.transactionDate,
                    description: `${mutation.description} #${idx + 1}`,
                  })),
                );
              }}
              variant="primary-dark">
              {t('confirmNumber')}
            </Button>
          </div>
        )}
        {isTransactionView && (
          <div className="flex flex-col gap-4 pt-4">
            {transactions.map((ts, index) => (
              <div className="flex items-center gap-2">
                <span className="text-nowrap">{t('transactionN', { number: index + 1 })}</span>
                <Input
                  key={`${index}_inputTns`}
                  type="text"
                  value={ts.description}
                  onChange={(e) =>
                    setTransactions((prevTrs) => {
                      if (!prevTrs) return [];
                      const newTransactions = [...prevTrs];
                      newTransactions[index] = {
                        ...newTransactions[index],
                        description: e.target.value,
                      };
                      return newTransactions;
                    })
                  }
                />
                <Input
                  key={`${index}_inputTns`}
                  type="text"
                  value={ts.amount as unknown as string}
                  onChange={(e) =>
                    setTransactions((prevTrs) => {
                      if (!prevTrs) return [];
                      const newTransactions = [...prevTrs];
                      newTransactions[index] = {
                        ...newTransactions[index],
                        amount: e.target.value as unknown as number,
                      };
                      return newTransactions;
                    })
                  }
                />
              </div>
            ))}
            <p>
              {t('remainingAmount', {
                amount:
                  Math.abs(mutation.amount / 100) - transactions.reduce((a, b) => a + +b.amount, 0),
              })}
            </p>
            <Button
              className="self-end"
              onClick={() => {
                onTransactionSplit(
                  transactions.map((tns) => ({
                    ...tns,
                    amount:
                      (mutation.amount < 0 ? -1 : 1) *
                      Number.parseFloat(tns.amount as unknown as string) *
                      100,
                  })),
                );
              }}
              disabled={
                transactions.some((tns) => tns.amount <= 0) ||
                transactions.reduce((a, b) => a + +b.amount, 0) !== Math.abs(mutation.amount) / 100
              }
              variant="primary-dark">
              {t('confirmSplit')}
            </Button>
          </div>
        )}
      </div>
    </ModalLayout>
  );
}
